// https://en.wikipedia.org/wiki/Parsing

import { TextSelectionKind } from './text.model';
import { TextViewNode, TextViewNodeKind } from './text.view.div.ast';
import { TextViewTokenKind, TextViewTokenWalker } from './text.view.div.lexer';

class ParseError {
  constructor(public message: string = 'unknown reason')
  {}

  public toString(): string
  {
    return 'text parsing failed: ' + this.message;
  }
}

function parseWordSequence(pos: TextViewTokenWalker): TextViewNode
{
  const words = [];
  while (pos.current && pos.current.kind === TextViewTokenKind.Text) {
    words.push(pos.current.content);
    pos.advance();
  }

  return {
    kind: TextViewNodeKind.WordSequence,
    content: words.join(''),
  };
}

function parseTag(pos: TextViewTokenWalker, tag: string): TextViewNode
{
  pos.advance();

  const parseChildren = () => {
    const children = [];

    for (let child; child = tryParse(pos); ) {
      children.push(child);
    }

    return children;
  };

  let node: TextViewNode | undefined = undefined;

  switch (tag.toLowerCase()) {
    case 'div':
    case 'p':
      node = {
        kind: TextViewNodeKind.Line,
        content: '',
        children: parseChildren(),
      };
    break;

    case 'br':
      node = {
        kind: TextViewNodeKind.EmptyWord,
        content: '',
      };
    break;

    case 'span':
      node = {
        kind: TextViewNodeKind.Proxy,
        content: '',
        children: parseChildren(),
      };
    break;

    default: throw new ParseError('unexpected tag: ' + tag);
  }

  pos.expect(TextViewTokenKind.LeaveTag);
  pos.advance();

  return node;
}

function parseSelection(pos: TextViewTokenWalker): TextViewNode
{
  const kind =
    pos.current.kind === TextViewTokenKind.SelectionAnchor
      ? TextSelectionKind.Anchor
      : TextSelectionKind.Focus;

  pos.advance();

  return {
    kind: TextViewNodeKind.Selection,
    content: '',
    selection: kind,
  };
}

function tryParse(pos: TextViewTokenWalker): TextViewNode | undefined
{
  if (! pos.current) {
    throw new ParseError('unexpected end of tokens');
  }

  switch (pos.current.kind) {
    case TextViewTokenKind.Text: return parseWordSequence(pos);
    case TextViewTokenKind.EnterTag: return parseTag(pos, pos.current.content);
    case TextViewTokenKind.SelectionAnchor: return parseSelection(pos);
    case TextViewTokenKind.SelectionFocus: return parseSelection(pos);
    default: return undefined;
  }
}

export function parse(pos: TextViewTokenWalker): TextViewNode
{
  const result = tryParse(pos);

  if (! result) {
    throw new ParseError('could not parse root');
  }

  if (pos.next) {
    throw new ParseError('tokens left over');
  }

  return result;
}



// WEBPACK FOOTER //
// ./src/app/text/text.view.div.parser.ts